// QuotesFunnelView.jsx — BM-H77 Phase-2 Reports tab.
//
// Rendered inside Reports.jsx as an additional tab. Reads the funnel
// endpoint wired in Phase-1 (Item confirmed in HANDOFF):
//
//   GET /api/hub/reporting/hub/reports/quotes-funnel
//     ?date_from=YYYY-MM-DD&date_to=YYYY-MM-DD&fee_earner=<email>
//
// Response shape (Phase-1):
//   {
//     "weeks": [{ "iso_week": "2026-W29", "sent": 4, "received": 3,
//                 "accepted": 2, "converted": 1 }, ...],
//     "by_fee_earner": [{ "fee_earner": "Emily Howard", "sent": 12, ...}, ...],
//     "totals": { "sent": 20, "received": 15, "accepted": 8, "converted": 4 }
//   }
//
// CSV export routes via the existing Reports export whitelist entry
// wired in Phase-1 (`GET /api/hub/reports/export/quotes-funnel`).

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";
import { API } from "../config/api";
import { Button } from "../components/ui/button";
import { parseBlobError } from "../utils/parseBlobError";

import { errorMessage } from "../utils/errorMessage";
function authHeaders() {
  const uid = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return { "X-Bonsai-User-Id": uid };
}

function pct(num, denom) {
  const n = Number(num) || 0;
  const d = Number(denom) || 0;
  if (d === 0) return "—";
  return `${Math.round((n / d) * 100)}%`;
}

export default function QuotesFunnelView({ dateFrom, dateTo, feeEarner }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchFunnel = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (feeEarner) params.set("fee_earner", feeEarner);
      const r = await axios.get(
        `${API}/hub/reporting/hub/reports/quotes-funnel?${params}`,
        { headers: authHeaders() },
      );
      setData(r.data);
    } catch (err) {
      setError(errorMessage(err, "Failed to load quotes funnel."));
      toast.error("Failed to load quotes funnel.");
    } finally { setLoading(false); }
  }, [dateFrom, dateTo, feeEarner]);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  const onExport = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (feeEarner) params.set("fee_earner", feeEarner);
      const resp = await axios.get(
        `${API}/hub/reports/export/quotes-funnel?${params}`,
        { headers: authHeaders(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `caseminder_quotes-funnel_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Quotes funnel exported.");
    } catch (err) {
      const detail = await parseBlobError(err);
      toast.error(`Export failed: ${detail}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16 text-txt-muted gap-2"
      data-testid="quotes-funnel-loading">
      <Loader2 className="w-5 h-5 animate-spin" /><span>Loading quotes funnel...</span>
    </div>;
  }
  if (error) {
    return <div className="bg-status-red-dim/20 border border-status-red/40 rounded-md p-4 text-sm text-status-red"
      data-testid="quotes-funnel-error">
      {error}
    </div>;
  }
  const weeks = Array.isArray(data?.weeks) ? data.weeks : [];
  const byFe  = Array.isArray(data?.by_fee_earner) ? data.by_fee_earner : [];
  const totals = data?.totals || {};
  const isEmpty = weeks.length === 0 && byFe.length === 0;

  if (isEmpty) {
    return <div className="glass-card rounded-md p-8 text-center text-xs text-txt-muted"
      data-testid="quotes-funnel-empty">
      No quotes activity in the selected range.
    </div>;
  }

  return (
    <div data-testid="quotes-funnel-view">
      <div className="flex justify-end mb-3">
        <Button size="sm" variant="outline" onClick={onExport}
          data-testid="quotes-funnel-export-csv"
          className="text-txt-muted border-navy-border gap-1.5">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Totals cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4" data-testid="quotes-funnel-totals">
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-white" data-testid="quotes-funnel-totals-sent">{totals.sent || 0}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Sent</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-teal">{totals.received || 0}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Received</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-status-amber">{totals.accepted || 0}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Accepted</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-teal">{totals.converted || 0}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Converted</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-lg font-bold text-white">
            {pct(totals.accepted, totals.sent)}
            <span className="text-txt-muted"> / </span>
            {pct(totals.converted, totals.accepted)}
          </p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">
            Accept / Convert rate
          </p>
        </div>
      </div>

      {/* Weekly grid */}
      {weeks.length > 0 && (
        <div className="glass-card rounded-md p-4 mb-4" data-testid="quotes-funnel-weeks">
          <h4 className="text-xs font-bold text-txt-muted uppercase tracking-wider mb-3">Weekly breakdown</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-txt-muted border-b border-navy-border">
                  <th className="py-2 pr-3">Week</th>
                  <th className="py-2 px-2">Sent</th>
                  <th className="py-2 px-2">Received</th>
                  <th className="py-2 px-2">Accepted</th>
                  <th className="py-2 px-2">Converted</th>
                  <th className="py-2 px-2">Accept rate</th>
                  <th className="py-2 px-2">Convert rate</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map(w => (
                  <tr key={w.iso_week} className="border-b border-navy-border/40"
                    data-testid={`quotes-funnel-week-${w.iso_week}`}>
                    <td className="py-1.5 pr-3 font-mono text-white">{w.iso_week}</td>
                    <td className="py-1.5 px-2 text-white">{w.sent || 0}</td>
                    <td className="py-1.5 px-2 text-teal">{w.received || 0}</td>
                    <td className="py-1.5 px-2 text-status-amber">{w.accepted || 0}</td>
                    <td className="py-1.5 px-2 text-teal">{w.converted || 0}</td>
                    <td className="py-1.5 px-2 text-txt-muted">{pct(w.accepted, w.sent)}</td>
                    <td className="py-1.5 px-2 text-txt-muted">{pct(w.converted, w.accepted)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per fee earner breakdown */}
      {byFe.length > 0 && (
        <div className="glass-card rounded-md p-4" data-testid="quotes-funnel-by-fe">
          <h4 className="text-xs font-bold text-txt-muted uppercase tracking-wider mb-3">By fee earner</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-txt-muted border-b border-navy-border">
                <th className="py-2 pr-3">Fee earner</th>
                <th className="py-2 px-2">Sent</th>
                <th className="py-2 px-2">Received</th>
                <th className="py-2 px-2">Accepted</th>
                <th className="py-2 px-2">Converted</th>
                <th className="py-2 px-2">Accept rate</th>
              </tr>
            </thead>
            <tbody>
              {byFe.map(fe => (
                <tr key={fe.fee_earner} className="border-b border-navy-border/40"
                  data-testid={`quotes-funnel-fe-${fe.fee_earner}`}>
                  <td className="py-1.5 pr-3 text-white">{fe.fee_earner}</td>
                  <td className="py-1.5 px-2 text-white">{fe.sent || 0}</td>
                  <td className="py-1.5 px-2 text-teal">{fe.received || 0}</td>
                  <td className="py-1.5 px-2 text-status-amber">{fe.accepted || 0}</td>
                  <td className="py-1.5 px-2 text-teal">{fe.converted || 0}</td>
                  <td className="py-1.5 px-2 text-txt-muted">{pct(fe.accepted, fe.sent)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
